import { ChunkData, EnvironmentElement, RENDER_DISTANCE } from "../../../../shared/src/environment.types";
import { Socket } from "socket.io-client";
import { RESPAWN_ZONE_CONFIG } from "../config";
import * as THREE from "three";

export interface RespawnPoint {
  position: THREE.Vector3;
  rotation: number;
}

interface ChunkPayload {
  chunks: ChunkData[];
  hash?: string;
}

export class EnvironmentService {
  private socket: Socket;
  private loadedChunks: Set<string> = new Set();
  private lastChunkHash?: string;

  constructor(socket: Socket) {
    console.log("Initializing EnvironmentService with socket:", {
      isConnected: socket?.connected,
      hasSocket: !!socket,
    });

    if (!socket) {
      throw new Error("Socket is required for EnvironmentService");
    }

    this.socket = socket;
    this.setupSocketListeners();
  }

  private setupSocketListeners(): void {
    console.log("Setting up environment socket listeners");

    this.socket.on("environment:chunkData", (payload: ChunkPayload) => {
      const chunks = payload?.chunks ?? [];
      this.lastChunkHash = payload?.hash || JSON.stringify(chunks);

      console.log("Received chunk data:", {
        numberOfChunks: chunks.length,
        chunkIds: chunks.map((c) => c.chunkId),
        hash: payload?.hash,
      });

      document.dispatchEvent(
        new CustomEvent("environment:chunksReceived", {
          detail: { chunks, hash: this.lastChunkHash },
        })
      );
    });

    // Dodajemy listener na błędy
    this.socket.on("error", (error: any) => {
      console.error("Socket error in EnvironmentService:", error);
    });

    // Dodajemy listener na rozłączenie
    this.socket.on("disconnect", () => {
      console.warn("Socket disconnected in EnvironmentService");
    });
  }

  public requestChunks(position: { x: number; z: number }): void {
    console.log("Requesting chunks for position:", position);

    this.socket.emit("environment:requestChunks", {
      position,
      renderDistance: RENDER_DISTANCE,
      clientHash: this.lastChunkHash,
    });
  }

  public reportTreeBreak(treeId: string, impactDirection: { x: number; y: number; z: number }): void {
    console.log("Reporting tree break:", {
      treeId,
      impactDirection,
    });

    this.socket.emit("environment:treeBreak", {
      treeId,
      impactDirection,
    });
  }

  public getRandomRespawnPoint(): RespawnPoint {
    // Pobierz referencję do respawn zone z gameState
    const respawnZone = window.gameState?.scene?.getObjectByName("respawnZone");
    if (!respawnZone) {
      console.error("[EnvironmentService] Respawn zone not found in scene");
      // Fallback na pozycję (0,0) jeśli nie znaleziono strefy
      return {
        position: new THREE.Vector3(0, 0, 0),
        rotation: Math.random() * Math.PI * 2,
      };
    }

    // Generuj losową pozycję w obrębie zielonego kwadratu (respawn zone)
    const randomX = (Math.random() - 0.5) * RESPAWN_ZONE_CONFIG.SIZE;
    const randomZ = (Math.random() - 0.5) * RESPAWN_ZONE_CONFIG.SIZE;

    // Uwzględnij pozycję strefy respawnu
    const respawnPoint: RespawnPoint = {
      position: new THREE.Vector3(
        respawnZone.position.x + randomX,
        0, // Zawsze na poziomie gruntu
        respawnZone.position.z + randomZ
      ),
      rotation: Math.random() * Math.PI * 2, // Losowy obrót 0-360 stopni
    };

    console.log("[EnvironmentService] Generated random respawn point:", {
      zonePosition: respawnZone.position.toArray(),
      zoneSize: RESPAWN_ZONE_CONFIG.SIZE,
      offset: [randomX, randomZ],
      finalPosition: respawnPoint.position.toArray(),
      rotation: ((respawnPoint.rotation * 180) / Math.PI).toFixed(2) + "°",
    });

    return respawnPoint;
  }
}
